import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

const priceFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Backend parayı her zaman "1250.00" biçiminde string döner.
 * Frontend bu değeri hesaplamada KULLANMAZ, yalnızca gösterir.
 */
export function formatPrice(value: string | number): string {
  const numeric = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(numeric) ? priceFormatter.format(numeric) : '—';
}

/**
 * Yalnızca GÖSTERİM amaçlı toplam. Kuruş tamsayısı üzerinden toplanır ki
 * "0.1 + 0.2" tuzağına düşülmesin.
 *
 * Bağlayıcı tutar her zaman backend'de hesaplanır: sipariş oluşturulurken
 * frontend'in gönderdiği toplam kabul edilmez (PRODUCT-DECISIONS §3.4).
 */
export function sumPrices(...values: Array<string | null | undefined>): string {
  const kurus = values.reduce<number>((total, value) => {
    if (!value) {
      return total;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? total + Math.round(numeric * 100) : total;
  }, 0);

  return (kurus / 100).toFixed(2);
}

const dateFormatter = new Intl.DateTimeFormat('tr-TR', {
  day: 'numeric',
  month: 'long',
  weekday: 'long',
});

/** "2026-08-10" → "10 Ağustos Pazartesi" */
export function formatDateKey(dateKey: string): string {
  const parsed = new Date(`${dateKey}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? dateKey : dateFormatter.format(parsed);
}

/** 195 → "3 sa 15 dk" — teslimat geri sayımı için */
export function formatCountdown(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (hours <= 0) {
    return `${rest} dk`;
  }

  return rest > 0 ? `${hours} sa ${rest} dk` : `${hours} sa`;
}

/**
 * WhatsApp bağlantısı için numarayı ULUSLARARASI biçime çevirir.
 *
 * wa.me ülke kodu ister; yerel yazımla ("0533 000 00 12") üretilen bağlantı
 * açılmaz. Türkiye numaraları için:
 *   "0533 000 00 12"  → 905330000012
 *   "533 000 00 12"   → 905330000012
 *   "+90 533 0000012" → 905330000012
 */
export function toWhatsAppNumber(phone: string): string {
  const digits = phone.replace(/\D/g, '');

  if (digits.startsWith('90') && digits.length === 12) {
    return digits;
  }

  if (digits.startsWith('0') && digits.length === 11) {
    return `90${digits.slice(1)}`;
  }

  if (digits.length === 10) {
    return `90${digits}`;
  }

  return digits;
}

/** `tel:` bağlantısı için numara — yerel biçim yeterlidir. */
export function toTelNumber(phone: string): string {
  return phone.replace(/[^\d+]/g, '');
}

export function buildWhatsAppUrl(phone: string, message: string): string {
  return `https://wa.me/${toWhatsAppNumber(phone)}?text=${encodeURIComponent(message)}`;
}

/** "Merhaba, {{productName}} hakkında..." şablonunu doldurur. */
export function renderTemplate(
  template: string,
  values: Record<string, string>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) =>
    key in values ? values[key] : '',
  );
}
